// DJI Pilot 2's JavaScript bridge, `window.djiBridge`, as one typed module.
//
// Every bridge call answers a JSON string `{"code": 0, "message": …, "data": …}`.
// `raw` keeps that string (the per-start report quotes Pilot's answers as
// they came), `reply` parses it, and `data` throws a BridgeError naming the
// call when the code is not 0, so a failure says which call it was.

export interface DjiReply {
  code: number;
  message?: string | null;
  data?: unknown;
}

export class BridgeError extends Error {
  constructor(
    readonly call: string,
    readonly reply: DjiReply | string,
  ) {
    super(`${call}: ${typeof reply === 'string' ? reply : JSON.stringify(reply)}`);
    this.name = 'BridgeError';
  }
}

export type BridgeObject = Record<string, unknown>;

declare global {
  interface Window {
    djiBridge?: BridgeObject;
    // thingConnect names its callback by string; Pilot 2 calls it on window.
    connectCallback?: (arg: unknown) => void;
  }
}

/** The bridge, or undefined outside DJI Pilot 2 (an ordinary browser). */
export function findBridge(w: Window = window): Bridge | undefined {
  return w.djiBridge ? new Bridge(w.djiBridge) : undefined;
}

export class Bridge {
  constructor(private readonly b: BridgeObject) {}

  /** The bridge's answer as it came. Throws only when the call itself throws. */
  raw(name: string, ...args: unknown[]): string {
    const fn = this.b[name];
    if (typeof fn !== 'function') throw new BridgeError(name, 'not a bridge function');
    const out: unknown = (fn as (...a: unknown[]) => unknown).apply(this.b, args);
    return typeof out === 'string' ? out : JSON.stringify(out);
  }

  reply(name: string, ...args: unknown[]): DjiReply {
    const text = this.raw(name, ...args);
    try {
      const parsed = JSON.parse(text) as DjiReply;
      if (typeof parsed?.code !== 'number') throw new Error('no code');
      return parsed;
    } catch {
      throw new BridgeError(name, text);
    }
  }

  data(name: string, ...args: unknown[]): unknown {
    const r = this.reply(name, ...args);
    if (r.code !== 0) throw new BridgeError(name, r);
    return r.data;
  }

  // Platform
  verifyLicense(appId: string, appKey: string, license: string): boolean {
    return String(this.data('platformVerifyLicense', appId, appKey, license)).toLowerCase() === 'true';
  }
  isVerified(): boolean {
    return String(this.data('platformIsVerified')).toLowerCase() === 'true';
  }
  remoteControllerSn(): string {
    return String(this.data('platformGetRemoteControllerSN') ?? '');
  }
  aircraftSn(): string {
    return String(this.data('platformGetAircraftSN') ?? '');
  }
  /** Pilot's app version. Pilot answers a JSON string, {"modelVersion":…,"appVersion":"17.3.2.2"}. */
  version(): string {
    const d = this.data('platformGetVersion');
    try {
      const v = (typeof d === 'string' ? JSON.parse(d) : d) as { appVersion?: string } | null;
      if (v?.appVersion) return v.appVersion;
    } catch {
      /* not JSON: show it as it came */
    }
    return String(d ?? '');
  }
  logPath(): string {
    return String(this.data('platformGetLogPath') ?? '');
  }
  setLogEncryptKey(key: string): DjiReply {
    return this.reply('platformSetLogEncryptKey', key);
  }
  clearLogEncryptKey(): DjiReply {
    return this.reply('platformClearLogEncryptKey');
  }
  setInformation(platformName: string, title: string, desc: string): DjiReply {
    return this.reply('platformSetInformation', platformName, title, desc);
  }
  setWorkspaceId(uuid: string): DjiReply {
    return this.reply('platformSetWorkspaceId', uuid);
  }
  /** Throws a BridgeError with Pilot's code and message when Pilot refuses the module. */
  loadComponent(name: string, param: object): DjiReply {
    const r = this.reply('platformLoadComponent', name, JSON.stringify(param));
    if (r.code !== 0) throw new BridgeError(`platformLoadComponent ${name}`, r);
    return r;
  }
  unloadComponent(name: string): DjiReply {
    return this.reply('platformUnloadComponent', name);
  }
  /** False when Pilot says so or cannot answer. */
  isComponentLoaded(name: string): boolean {
    const r = this.reply('platformIsComponentLoaded', name);
    return r.code === 0 && r.data === true;
  }

  // Thing (the RC's MQTT session)
  thingGetConfigs(): DjiReply {
    return this.reply('thingGetConfigs');
  }
  thingConnect(user: string, password: string, callback: string): DjiReply {
    return this.reply('thingConnect', user, password, callback);
  }
  thingDisconnect(): DjiReply {
    return this.reply('thingDisconnect');
  }

  // API module
  apiGetHost(): DjiReply {
    return this.reply('apiGetHost');
  }
  apiGetToken(): DjiReply {
    return this.reply('apiGetToken');
  }

  // Live share
  liveshareSetConfig(type: number, params: object): DjiReply {
    return this.reply('liveshareSetConfig', type, JSON.stringify(params));
  }
  liveshareGetConfig(): DjiReply {
    return this.reply('liveshareGetConfig');
  }
  liveshareSetVideoPublishType(type: string): DjiReply {
    return this.reply('liveshareSetVideoPublishType', type);
  }
  liveshareGetStatus(): DjiReply {
    return this.reply('liveshareGetStatus');
  }

  // WebSocket module
  wsConnect(host: string, token: string, callback: string): DjiReply {
    return this.reply('wsConnect', host, token, callback);
  }
  wsGetConnectState(): DjiReply {
    return this.reply('wsGetConnectState');
  }

  liveshareStartLive(): DjiReply {
    return this.reply('liveshareStartLive');
  }
  liveshareStopLive(): DjiReply {
    return this.reply('liveshareStopLive');
  }
  /** The Flutter page's djisdk.js called wsConnect() here, so its Unload never disconnected. */
  wsDisconnect(): DjiReply {
    return this.reply('wsDisconnect');
  }

  // Media
  mediaSetAutoUploadPhoto(on: boolean): DjiReply {
    return this.reply('mediaSetAutoUploadPhoto', on);
  }
  mediaSetAutoUploadVideo(on: boolean): DjiReply {
    return this.reply('mediaSetAutoUploadVideo', on);
  }
  /** 0 original, 1 thumbnail. */
  mediaSetUploadPhotoType(type: number): DjiReply {
    return this.reply('mediaSetUploadPhotoType', type);
  }
  /** Dual-RC mode: 0 this RC (mine), 1 the other. */
  mediaSetDownloadOwner(owner: number): DjiReply {
    return this.reply('mediaSetDownloadOwner', owner);
  }
  mediaGetDownloadOwner(): DjiReply {
    return this.reply('mediaGetDownloadOwner');
  }
  mediaGetAutoUploadPhoto(): DjiReply {
    return this.reply('mediaGetAutoUploadPhoto');
  }
  mediaGetAutoUploadVideo(): DjiReply {
    return this.reply('mediaGetAutoUploadVideo');
  }
  mediaGetUploadPhotoType(): DjiReply {
    return this.reply('mediaGetUploadPhotoType');
  }
}
